import API from '../api/api';
import { ApiRoutes } from '../constans/api';

export type ImportKind = 'participants' | 'reportingGroups' | 'users';

export type ImportTemplateFormat = 'csv' | 'xlsx';

export interface IImportRowError {
  row: number | null;
  errors: string[];
}

export interface IImportResult {
  status: 'success' | 'failed';
  message: string;
  summary: {
    created: number;
    updated: number;
    skipped: number;
  };
  rowErrors: IImportRowError[];
}

interface IImportRejection {
  details?: { file?: string[] };
}

interface IImportErrorResponse {
  status?: number;
  data?: IImportResult | IImportRejection;
}

const isImportResult = (data: IImportErrorResponse['data']): data is IImportResult =>
  !!data && Array.isArray((data as IImportResult).rowErrors);

const resourceRoute: Record<ImportKind, ApiRoutes> = {
  participants: ApiRoutes.participants,
  reportingGroups: ApiRoutes.reportingGroups,
  users: ApiRoutes.shareUsers,
};

const templateFilename: Record<ImportKind, string> = {
  participants: 'participants-template',
  reportingGroups: 'reporting-groups-template',
  users: 'report-access-template',
};

const buildUrl = (kind: ImportKind, surveyId: string, route: ApiRoutes) =>
  `${ApiRoutes.surveyList}${surveyId}/${resourceRoute[kind]}${route}`;

/**
 * Uploads a CSV/XLSX file.
 *
 * The backend answers 200 when the file was processed, 422 when its contents
 * were rejected, and 400 when the upload never reached the importer. All three
 * are normalised into an IImportResult so the caller has one thing to render.
 */
export const postImport = async (
  kind: ImportKind,
  surveyId: string,
  file: File
): Promise<IImportResult> => {
  const body = new FormData();
  body.append('file', file);

  try {
    // Content-Type is left to axios so the multipart boundary is generated.
    const { data } = await API.post(buildUrl(kind, surveyId, ApiRoutes.import), body);
    return data;
  } catch (error) {
    const response = (error as IImportErrorResponse) ?? {};

    // The file was read but rejected: the body is already an import result.
    if (response.status === 422 && isImportResult(response.data)) {
      return response.data;
    }

    // The file never reached the importer (wrong type, too large, empty). The
    // interceptor suppresses the global toast for these URLs, so shape it into
    // a result the modal can render rather than failing silently.
    const rejection = (response.data as IImportRejection | undefined)?.details?.file;
    if (response.status === 400 && rejection?.length) {
      return {
        status: 'failed',
        message: rejection[0],
        summary: { created: 0, updated: 0, skipped: 0 },
        rowErrors: [{ row: null, errors: rejection }],
      };
    }

    throw error;
  }
};

export const downloadImportTemplate = async (
  kind: ImportKind,
  surveyId: string,
  fileFormat: ImportTemplateFormat
): Promise<void> => {
  const { data } = await API.get(buildUrl(kind, surveyId, ApiRoutes.importTemplate), {
    params: { fileFormat },
    responseType: 'blob',
  });

  const objectUrl = URL.createObjectURL(data as Blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `${templateFilename[kind]}.${fileFormat}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};
